import { Component, Input } from '@angular/core';
import { ArgyleIncomeStream } from 'src/app/models/Argyle';
import { WorkNumberDetail } from 'src/app/models/income-wizard-workflow';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/services/auth.service';
import { ApplicantDataService } from 'src/app/services/applicant-data.service';
import { GroupApplicantsService } from 'src/app/services/group-applicants.service';
import * as moment from 'moment';

@Component({
  selector: 'app-work-number',
  templateUrl: './work-number.component.html',
  styleUrls: ['./work-number.component.less']
})
export class WorkNumberComponent {
  @Input() stream :ArgyleIncomeStream;
  @Input() detail: WorkNumberDetail | null = null;
  constructor(public modalService: NgbModal, private toast: ToastrService, private auth: AuthService, public appData: ApplicantDataService,public groups: GroupApplicantsService) { }

  public convertDate(str: string)
  {
    const date = moment(str, "YYYYMMDDHHmmss").toDate();
    return date;
  }

  // End date from The Work Number payload. The API may send a string, an empty object, or
  //a one-element array like [{ "#text": "20111031000000" }]. We only ever feed a plain string into moment.
  public endEmploymentDate(record: any): Date | null {
    const raw = record?.tsvemployeeV100?.dtendemployment;
    const timestamp = this.extractEndEmploymentTimestampString(raw);
    return timestamp ? this.parseTwnDateStamp(timestamp) : null;
  }

  // `dtendemployment` is not always a plain string in the JSON we receive.
  //Normal case: `"20111031000000"`
  //Alternate case: `[{ "#text": "20111031000000" }]`
  private extractEndEmploymentTimestampString(raw: unknown): string | null {
    if (raw == null || raw === '') {
      return null;
    }

    if (typeof raw === 'string') {
      return this.nonEmptyTimestampOrNull(raw);
    }

    if (Array.isArray(raw) && raw.length > 0) {
      const first = raw[0] as Record<string, unknown> | undefined;
      const embedded = first?.['#text'];
      if (embedded == null) {
        return null;
      }
      return this.nonEmptyTimestampOrNull(String(embedded));
    }

    return null;
  }

  // At least 8 chars so we do not pass garbage (e.g. `"2011"`) into the date parser.
  private nonEmptyTimestampOrNull(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length >= 8 ? trimmed : null;
  }

  private parseTwnDateStamp(stamp: string): Date | null {
    const m = moment(stamp, 'YYYYMMDDHHmmss', true);
    return m.isValid() ? m.toDate() : null;
  }

  /** Number of pay periods per year for a given pay-period frequency message/code. */
  public payPeriodsPerYear(record: any): number | null {
    const freq = record?.tsvbasecomp?.tsvpayperiodfrequency;
    if (!freq) { return null; }
    const msg = (freq.message || '').toString().toLowerCase();
    const code = freq.code != null ? freq.code.toString() : '';
    const map: { [k: string]: number } = {
      weekly: 52,
      biweekly: 26,
      'bi-weekly': 26,
      semimonthly: 24,
      'semi-monthly': 24,
      monthly: 12,
      quarterly: 4,
      annually: 1,
      yearly: 1,
      daily: 260
    };
    if (map[msg] != null) { return map[msg]; }
    const codeMap: { [k: string]: number } = {
      '1': 52, '2': 26, '3': 24, '4': 12, '5': 4, '6': 26
    };
    return codeMap[code] || null;
  }

  /** Periods-per-year for TSVPAYFREQUENCY (the denomination of the stated rate). */
  public ratePeriodsPerYear(record: any): number | null {
    const code = record?.tsvbasecomp?.tsvpayfrequency?.code;
    if (code == null || code === '') { return null; }
    const map: { [k: string]: number } = {
      '1': 1, '2': 2, '3': 4, '4': 12, '5': 24, '6': 26, '7': 52, '8': 260, '9': 2080
    };
    return map[code.toString()] ?? null;
  }

  /** Gross per pay period — only meaningful for hourly workers (rate × avg hours). */
  public grossPerPayPeriod(record: any): number | null {
    if (!this.isHourly(record)) { return null; }
    const base = record?.tsvbasecomp;
    if (!base) { return null; }
    const rate = parseFloat(base.tsvrateofpay);
    const hrs = parseFloat(base.tsvavghrsworked);
    if (isNaN(rate) || isNaN(hrs)) { return null; }
    return rate * hrs;
  }

  /** Estimated monthly gross: hourly uses rate×hrs×periods/12, others annualize the stated rate. */
  public estimatedMonthlyGross(record: any): number | null {
    if (this.isHourly(record)) {
      const perPeriod = this.grossPerPayPeriod(record);
      const periods = this.payPeriodsPerYear(record);
      if (perPeriod == null || periods == null) { return null; }
      return (perPeriod * periods) / 12;
    }
    const rate = parseFloat(record?.tsvbasecomp?.tsvrateofpay);
    const ratePeriods = this.ratePeriodsPerYear(record);
    if (isNaN(rate) || ratePeriods == null) { return null; }
    return (rate * ratePeriods) / 12;
  }

  public isHourly(record: any): boolean {
    const msg = record?.tsvbasecomp?.tsvpayfrequency?.message || '';
    return msg.toString().toLowerCase() === 'hourly';
  }

  public statusClass(record: any): string {
    const msg = (record?.tsvemployeeV100?.employeestatus?.message || '').toLowerCase();
    if (msg === 'active') { return 'status-active'; }
    if (msg.indexOf('terminat') >= 0 || msg === 'inactive') { return 'status-inactive'; }
    return 'status-other';
  }

  public isDataStale(): boolean {
    return this.detail?.isDataStale === true || this.hasFailedCheck('DataFreshness');
  }

  public staleReason(): string {
    return this.detail?.staleReason || this.getCheckReason('DataFreshness') || 'Employment data is too old to be used for income verification.';
  }

  public isEmploymentInactive(): boolean {
    return this.detail?.isEmploymentInactive === true || this.hasFailedCheck('EmploymentStatus');
  }

  public isNameMismatch(): boolean {
    return this.detail?.isNameMismatch === true || this.hasFailedCheck('NameMatch');
  }

  public get failedSopChecks() {
    return this.stream?.sopChecks?.filter(c => !c.passed) ?? [];
  }

  private hasFailedCheck(name: string): boolean {
    return this.stream?.sopChecks?.some(c => c.checkName === name && !c.passed) ?? false;
  }

  public getCheckReason(name: string): string | null {
    return this.stream?.sopChecks?.find(c => c.checkName === name && !c.passed)?.reason ?? null;
  }
}
